import {useMemo} from "react";
import PropTypes from 'prop-types';
import {
    CElementCover,
    CForm,
    CFormGroup,
    CInput,
    CLabel,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader, CSelect,
    CSwitch,
} from "@coreui/react";
import noop from "../../utils/noop";
import { useTranslation } from "react-i18next";
import {Formik, Field } from "formik";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faLayerGroup } from "@fortawesome/pro-light-svg-icons";

import { SaveButton, CancelButton} from "../Buttons";
import * as Yup from "yup";
import CommonRecordData from "../CommonRecordData/CommonRecordData";

export const ProductGroupFormModal = ({record, onSaveClick, onCancelClick, show, loading, onClose, areas}) => {
    
    const {t} = useTranslation(["ProductGroupFormModal", "Buttons", "Validation"]);
    
    const validationSchema = useMemo( () => Yup.object({
        name: Yup.string().required(t("Validation:fieldRequired")),
        rccp: Yup.string().required(t("Validation:fieldRequired")),
        work_center: Yup.string().required(t("Validation:fieldRequired")),
        gl_cat: Yup.string().required(t("Validation:fieldRequired")),
        areas_id: Yup.number().moreThan(0,t("Validation:pleaseSelectValue"))
    }),[t]);
    
    return (
        <Formik initialValues={record}
                validateOnChange
                validateOnMount={parseInt(record.id) > 0}
                enableReinitialize
                onSubmit={onSaveClick}
                validationSchema={validationSchema}
        >
            {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  resetForm,
                  isValid,
                  setFieldTouched,
                  validateField
                  /* and other goodies */
              }) => {
                
                return <CModal show={show} closeOnBackdrop={false} onClose={onClose} size="lg">
                    <CModalHeader closeButton><h2 className="mr-2 d-flex flex-row align-items-center"><FontAwesomeIcon icon={faLayerGroup} className="mr-2"/> {t("productGroupEdit")}</h2></CModalHeader>
                    <CForm onSubmit={handleSubmit}>
                <CModalBody className="p-4">

                    <CFormGroup>
                        <CLabel htmlFor="name">{t("ProductGroupFormModal:name")}<sup>*</sup></CLabel>
                        <Field name="name">
                            {({
                                  field, // { name, value, onChange, onBlur },
                                  meta: {touched, error}
                              }) =>
                                <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error}/>
                            }
                        </Field>
                        <FieldErrorMessage name="name" />
                    </CFormGroup>
                    
                    <CFormGroup>
                        <CLabel htmlFor="rccp">{t("ProductGroupFormModal:rccp")}<sup>*</sup></CLabel>
                        <Field name="rccp">
                            {({
                                  field, // { name, value, onChange, onBlur },
                                  meta: {touched, error}
                              }) =>
                                <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error}/>
                            }
                        </Field>
                        <FieldErrorMessage name="rccp" />
                    </CFormGroup>
                
                    <CFormGroup>
                        <CLabel htmlFor="work_center">{t("ProductGroupFormModal:workCenter")}<sup>*</sup></CLabel>
                        <Field name="work_center">
                            {({
                                  field, // { name, value, onChange, onBlur },
                                  meta: {touched, error}
                              }) =>
                                <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error}/>
                            }
                        </Field>
                        <FieldErrorMessage name="work_center" />
                    </CFormGroup>

                    <CFormGroup>
                        <CLabel htmlFor="gl_cat">{t("ProductGroupFormModal:glCat")}<sup>*</sup></CLabel>
                        <Field name="gl_cat">
                            {({
                                  field, // { name, value, onChange, onBlur },
                                  meta: {touched, error}
                              }) =>
                                <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error}/>
                            }
                        </Field>
                        <FieldErrorMessage name="gl_cat" />
                    </CFormGroup>
                    
                    <CFormGroup>
                        <CLabel htmlFor="areas_id">{t("ProductGroupFormModal:area")}<sup>*</sup></CLabel>
                        <Field name="areas_id">
                            {({
                                  field, field: { value },
                                  meta: {touched, error}
                              }) =>

                                <CSelect {...field} invalid={error && touched} valid={touched && !error}>
                                    {areas.map( r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </CSelect>
                            }
                        </Field>
                        <FieldErrorMessage name="areas_id" />
                    </CFormGroup>
                    
                    <CFormGroup className="d-flex flex-row">
                        <Field type="checkbox" name="is_active">
                            {({field, field: { value }}) =>
                                    <CSwitch id="is_active" shape="pill" color="info" labelOn="✓" labelOff="✕" size="sm"
                                                variant="opposite"
                                                {...field} value={value === 0 ? true : false}/>
                            }
                        </Field>
                        <CLabel htmlFor="is_active" className="ml-2 user-select-none">{t("BOMFormModal:active")}</CLabel>
                    </CFormGroup>

                    {record.id > 0 && <CommonRecordData item={record} />}
                    
                </CModalBody>
                <CModalFooter>
                    <CancelButton type="button" onClick={onCancelClick}>{t("Buttons:cancel")}</CancelButton>
                    <SaveButton type="submit" color="primary" disabled={isValid===false}>{t("Buttons:save")}</SaveButton>
                </CModalFooter>
                    </CForm>
                    {loading && <CElementCover/>}
            </CModal>}}
        </Formik>
    );
};

ProductGroupFormModal.propTypes = {
    record: PropTypes.shape({
        id: PropTypes.number,
        is_active: PropTypes.number,
        name: PropTypes.string,
        areas_id: PropTypes.number,
        rccp: PropTypes.string,
        work_center: PropTypes.string,
        gl_cat: PropTypes.string
    }),
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    onSaveClick: PropTypes.func,
    onCancelClick: PropTypes.func,
};

ProductGroupFormModal.defaultProps = {
    loading: false,
    onChange: noop,
    onClose: noop,
    onSaveClick: noop,
    onCancelClick: noop,
};

export default ProductGroupFormModal;